import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircleIcon, ArrowLeftIcon } from "@phosphor-icons/react";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw1.length < 6) { setErr("Password must be at least 6 characters."); return; }
    if (pw1 !== pw2) { setErr("Passwords don't match."); return; }
    setSaving(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw1 });
      setDone(true);
      toast.success("Password updated");
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="reset-password-page">
      <Navbar />
      <main className="flex-1 max-w-md mx-auto w-full px-6 py-16">
        {done ? (
          <div className="text-center" data-testid="reset-success-state">
            <div className="h-14 w-14 mx-auto bg-emerald-100 text-emerald-700 rounded-full grid place-items-center mb-4">
              <CheckCircleIcon size={32} weight="fill" />
            </div>
            <h1 className="font-display font-black text-3xl tracking-tight">Password updated</h1>
            <p className="text-zinc-600 mt-3 text-sm">
              You&apos;re all set. Sign in with your new password.
            </p>
            <Button
              onClick={() => navigate("/login")}
              className="mt-6 bg-[#1D78AB] hover:bg-[#165d85] text-white"
              data-testid="reset-go-login-btn"
            >
              Go to sign in
            </Button>
          </div>
        ) : (
          <>
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 mb-6"
            >
              <ArrowLeftIcon size={14} /> Back to sign in
            </Link>
            <h1 className="font-display font-black text-4xl tracking-tight">Set a new password</h1>
            <p className="text-zinc-600 mt-2 text-sm">
              Pick something strong. At least 6 characters.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <Input
                type="password"
                required
                placeholder="New password"
                value={pw1}
                onChange={(e) => setPw1(e.target.value)}
                className="h-11"
                data-testid="reset-pw1-input"
              />
              <Input
                type="password"
                required
                placeholder="Confirm new password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                className="h-11"
                data-testid="reset-pw2-input"
              />
              {err && <p className="text-sm text-red-600" data-testid="reset-error">{err}</p>}
              <Button
                type="submit"
                disabled={saving}
                className="w-full h-11 bg-[#1D78AB] hover:bg-[#165d85] text-white"
                data-testid="reset-submit-btn"
              >
                {saving ? "Saving…" : "Update password"}
              </Button>
            </form>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
